import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Book, School, FileText, Flag, 
  TrendingUp, ArrowUpRight, ArrowDownRight, 
  BarChart2, Calendar, User
} from 'lucide-react';
import AuthContext from '../../contexts/AuthContext';

const ModeratorDashboardPage = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState({
    school: { name: '', id: '' },
    users: { total: 0, verified: 0, recent: 0 },
    academic: { departments: 0, courses: 0, sections: 0 },
    materials: { total: 0, notes: 0, exams: 0, syllabi: 0, other: 0, reported: 0, recent: 0 },
    ratings: { total: 0, average: 0, recent: 0 },
    professors: { total: 0 },
    reports: { pending: 0, total: 0 } // Add this line
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModeratorStats = async () => {
      try {
        setLoading(true);
        
        // Get the school ID from the user context
        const schoolId = user?.school?.id || user?.schoolId;
        
        if (!schoolId) {
          throw new Error('School information not found');
        }
        
        // 1. Fetch school details
        const schoolResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (!schoolResponse.ok) {
          throw new Error('Failed to fetch school details');
        }

        const schoolData = await schoolResponse.json();
        
        // 2. Fetch school stats
        const statsResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/stats`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (!statsResponse.ok) {
          throw new Error('Failed to fetch school statistics');
        }

        const statsData = await statsResponse.json();

        // 3. Fetch pending reports count
        let reportsCount = { pending: 0, total: 0 };
        try {
          const reportsResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports?status=pending`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          if (reportsResponse.ok) {
            const reportsData = await reportsResponse.json();
            if (Array.isArray(reportsData)) {
              reportsCount.pending = reportsData.length;
            } else if (reportsData.reports && Array.isArray(reportsData.reports)) {
              reportsCount.pending = reportsData.reports.length;
            } else if (typeof reportsData.total === 'number') {
              reportsCount.pending = reportsData.total;
            }
          }

          // Get total reports count including all types
          const allReportsResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/schools/${schoolId}/reports`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          if (allReportsResponse.ok) {
            const allReportsData = await allReportsResponse.json();
            if (Array.isArray(allReportsData)) {
              reportsCount.total = allReportsData.length;
            } else if (allReportsData.reports && Array.isArray(allReportsData.reports)) {
              reportsCount.total = allReportsData.reports.length;
            } else if (typeof allReportsData.total === 'number') {
              reportsCount.total = allReportsData.total;
            }
          }
        } catch (reportsError) {
          console.error('Error fetching reports count:', reportsError);
        }
        
        // Combine the data
        setStats({
          school: {
            id: schoolData.id,
            name: schoolData.name
          },
          ...statsData,
          reports: reportsCount
        });
        
      } catch (error) {
        console.error('Error fetching moderator statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'moderator') {
      fetchModeratorStats();
    }
  }, [user?.role, user?.schoolId]);

  const StatCard = ({ title, value, icon, change, link }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          <h3 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{loading ? '...' : value}</h3>
          
          {change && (
            <div className="mt-1 flex items-center">
              {change > 0 ? (
                <span className="text-green-500 flex items-center text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  {change}% increase
                </span>
              ) : (
                <span className="text-red-500 flex items-center text-sm">
                  <ArrowDownRight size={16} className="mr-1" />
                  {Math.abs(change)}% decrease
                </span>
              )}
              <span className="text-gray-500 dark:text-gray-400 text-xs ml-1">last 30 days</span>
            </div>
          )}
        </div>
        
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
          {icon}
        </div>
      </div>
      
      {link && (
        <div className="mt-4">
          <Link to={link} className="text-blue-600 hover:text-blue-700 text-sm font-medium">
            View details →
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Moderator Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {stats.school.name ? `Managing ${stats.school.name}` : 'Manage your school'}
        </p>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          School Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title="Students" 
            value={stats.users.total} 
            icon={<Users size={24} />}
            change={stats.users.recent > 0 ? Math.round((stats.users.recent / stats.users.total) * 100) : 0}
            link="/moderator/users"
          />
          <StatCard 
            title="Courses" 
            value={stats.academic.courses} 
            icon={<Book size={24} />}
            link="/moderator/courses"
          />
          <StatCard 
            title="Professors" 
            value={stats.professors.total} 
            icon={<User size={24} />}
            link="/moderator/professors"
          />
          <StatCard 
            title="Materials" 
            value={stats.materials.total} 
            icon={<FileText size={24} />}
            change={stats.materials.recent > 0 ? Math.round((stats.materials.recent / stats.materials.total) * 100) : 0}
            link="/moderator/materials"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reports</h3>
              <Link to="/moderator/reports" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                View all reports →
              </Link>
            </div>
            
            {stats.reports.pending > 0 ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-center">
                <Flag className="h-6 w-6 text-yellow-500 mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Attention Required</h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {stats.reports.pending} {stats.reports.pending === 1 ? 'report requires' : 'reports require'} your review.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center">
                <Flag className="h-6 w-6 text-green-500 mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-green-800 dark:text-green-200">All Clear</h4>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    No pending reports at this time.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Material Statistics</h3>
                <Link to="/moderator/materials" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  View all materials →
                </Link>
              </div>
              
              <div className="flex flex-wrap -mx-2">
                <div className="w-1/2 p-2">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Notes</span>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">{stats.materials.notes}</span>
                    </div>
                  </div>
                </div>
                <div className="w-1/2 p-2">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Exams</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">{stats.materials.exams}</span>
                    </div>
                  </div>
                </div>
                <div className="w-1/2 p-2">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Syllabi</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">{stats.materials.syllabi}</span>
                    </div>
                  </div>
                </div>
                <div className="w-1/2 p-2">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Other</span>
                      <span className="font-semibold text-purple-600 dark:text-purple-400">{stats.materials.other}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center mb-2">
                  <BarChart2 size={20} className="text-gray-400 mr-2" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Material Distribution</p>
                </div>
                
                <div className="relative pt-1">
                  <div className="flex h-4 overflow-hidden text-xs bg-gray-200 dark:bg-gray-600 rounded-full">
                    <div 
                      className="flex flex-col justify-center text-center text-white bg-blue-500" 
                      style={{ width: `${(stats.materials.notes / stats.materials.total) * 100}%` }}
                    ></div>
                    <div 
                      className="flex flex-col justify-center text-center text-white bg-red-500" 
                      style={{ width: `${(stats.materials.exams / stats.materials.total) * 100}%` }}
                    ></div>
                    <div 
                      className="flex flex-col justify-center text-center text-white bg-green-500" 
                      style={{ width: `${(stats.materials.syllabi / stats.materials.total) * 100}%` }}
                    ></div>
                    <div 
                      className="flex flex-col justify-center text-center text-white bg-purple-500" 
                      style={{ width: `${(stats.materials.other / stats.materials.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="flex text-xs mt-2 justify-between text-gray-600 dark:text-gray-400">
                  <span className="flex items-center">
                    <span className="w-2 h-2 inline-block mr-1 bg-blue-500 rounded-full"></span>
                    Notes
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 inline-block mr-1 bg-red-500 rounded-full"></span>
                    Exams
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 inline-block mr-1 bg-green-500 rounded-full"></span>
                    Syllabi
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 inline-block mr-1 bg-purple-500 rounded-full"></span>
                    Other
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Access</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link 
                  to="/moderator/courses" 
                  className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="mr-4 p-3 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                    <Book size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Manage Courses</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Add, edit courses</p>
                  </div>
                </Link>
                
                <Link 
                  to="/moderator/professors" 
                  className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="mr-4 p-3 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                    <User size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Manage Professors</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Add, edit professors</p>
                  </div>
                </Link>
                
                <Link 
                  to="/moderator/reports" 
                  className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="mr-4 p-3 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                    <Flag size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Review Reports</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {stats.reports.pending > 0 
                        ? `${stats.reports.pending} items need review` 
                        : 'No pending reports'}
                    </p>
                  </div>
                </Link>
                
                <Link 
                  to="/moderator/materials" 
                  className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="mr-4 p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                    <FileText size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Review Materials</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">View uploaded content</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <Calendar size={16} className="mr-1" />
                Last 30 days
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">New Users</h4>
                  <div className="text-green-500 flex items-center text-sm">
                    <TrendingUp size={16} className="mr-1" />
                    <span>{stats.users.recent}</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  +{stats.users.recent}
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">New Materials</h4>
                  <div className="text-green-500 flex items-center text-sm">
                    <TrendingUp size={16} className="mr-1" />
                    <span>{stats.materials.recent}</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  +{stats.materials.recent}
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Professor Ratings</h4>
                  <div className="text-green-500 flex items-center text-sm">
                    <TrendingUp size={16} className="mr-1" />
                    <span>{stats.ratings.recent}</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  +{stats.ratings.recent}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ModeratorDashboardPage;